import { useEffect, useState } from 'react';
import TransferModal from './TransferModal';
import CollaborateModal from './CollaborateModal';
import EmailModal from './EmailModal';
import FollowUpModal from './FollowUpModal';


interface ActionItem {
  id: number | string;
  title: string;
  color: string;
  icon?: string;
  active: boolean;
}
interface SelectedStudent {
  id: string;
  name: string;
  phone: string;
  passportNumber?: string;  // New optional field
}

interface ConfirmationModalProps {
  isOpen: boolean;
  isAdditional: boolean;
  onClose: () => void;
  onSave: () => void;
  selectedStudent: SelectedStudent;
  actions?: ActionItem[];
}

export default function ConfirmationModal(
  { isOpen, isAdditional, onClose, onSave, selectedStudent }: ConfirmationModalProps
) {

  const [isActionsOpen, setIsActionsOpen] = useState(false);

  // Modal states
  const [isTransfer, setIsTransfer] = useState(false);
  const [isCollaborate, setIsCollaborate] = useState(false);
  const [isGmail, setIsGmail] = useState(false);
  const [isFollowUp, setIsFollowUp] = useState(false);
  const [isSaving, setIsSaving] = useState(false);


  // Action items with different colors
  const actions = [
    { id: 1, title: "Transfer Lead", color: "#A5B4FC", icon: `${process.env.PUBLIC_URL}/images/desktop/TransferLead-purple.svg`, active: true },
    { id: 2, title: "Collaborate with others", color: "#0991B2", icon: `${process.env.PUBLIC_URL}/images/desktop/collaborate-teal.svg`, active: true },
    { id: 3, title: "Send Gmail", color: "#EF4544", icon: `${process.env.PUBLIC_URL}/images/desktop/gmailIcon.svg`, active: true },
    { id: 4, title: "Add Follow Up", color: "#3483BE", icon: `${process.env.PUBLIC_URL}/images/desktop/follow-up-leadMngt.svg`, active: true },
  ];

  useEffect(() => {
    // Open the action modal if isAdditional is true
    if (isAdditional) {
      setIsActionsOpen(true);
    } else {
      setIsActionsOpen(false);
    }
  }, [isAdditional]);

  const handleActionClick = (action: ActionItem) => {
    // Handle action click
    switch (action.id) {
      case 1:
        // Open transfer modal
        setIsTransfer(true);
        break;
      case 2:
        // Open collaborate modal
        setIsCollaborate(true);
        break;
      case 3:
        // Open Gmail modal
        setIsGmail(true);
        break;
      case 4:
        // Open follow up modal
        setIsFollowUp(true);
        break;
      default:
        // Handle other actions
        console.log(`Action clicked: ${action.title}`);
    }
  };

  const handleCloseAllModal = () => {
    // Close all modals
    onClose();
    setIsFollowUp(false);
    setIsActionsOpen(false);
    setIsTransfer(false);
    setIsCollaborate(false);
    setIsGmail(false);
    window.location.reload();
  }

  return (
    <div className="flex items-center justify-center p-4">
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl w-[60%] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-xl font-semibold text-gray-800">Confirm Changes</h2>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <img src={`${process.env.PUBLIC_URL}/images/desktop/close-btn.svg`} alt="Close" className="w-6 h-6" />
              </button>
            </div>

            {/* Main content */}
            <div className="p-5">
              <p className="text-gray-700 text-lg">Are you sure with the changes?</p>
            </div>

            {/* OR divider
            <div className="px-5 pb-3">
              <div className="relative flex items-center justify-center">
                <div className="border-t border-gray-300 absolute w-full"></div>
                <span className="bg-white px-4 relative z-10 text-gray-500 font-medium">OR</span>
              </div>
            </div> */}

            {/* Footer buttons - positioned below the dialog */}
            <div className="p-5 bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-5 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setIsSaving(true);
                  try {
                    await onSave(); // assume it's async or wrap in Promise.resolve
                  } finally {
                    setIsSaving(false);
                  }
                }}
                disabled={isSaving}
                className={`px-5 py-2 rounded-lg text-white transition-colors shadow-sm ${isSaving ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                  }`}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>

            </div>
          </div>
        </div>
      )}
      {isActionsOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl w-[40%] overflow-hidden">

            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b">
              <div></div>
              <h2 className="text-xl font-semibold text-gray-800">Additional Actions</h2>
              <button
                onClick={handleCloseAllModal}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <img src={`${process.env.PUBLIC_URL}/images/desktop/close-btn.svg`} alt="Close" className="w-6 h-6" />
              </button>
            </div>

            {/* Scrollable action boxes */}
            <div className="px-5 m-10">
              <div className="max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                <div className="grid grid-cols-2 gap-3">
                  {actions.map((action, index) => (
                    <div
                      key={index}
                      className={`rounded-lg p-3 cursor-pointer shadow-md hover:shadow-lg transition-shadow duration-200 flex items-center gap-4 ${action.active ? 'bg-white cursor-pointer' : 'bg-gray-200 opacity-50 cursor-not-allowed'}`}
                      title={action.active ? action.title : "This feature is coming soon"}
                      style={{ borderColor: action.color, borderWidth: '2px', borderStyle: 'solid', color: action.color }}
                      aria-disabled={!action.active}
                      onClick={() => {
                        // Handle action click
                        handleActionClick(action);
                      }}
                    >
                      <img
                        src={action.icon}
                        alt={action.title}
                        className="w-6 h-6"
                      />
                      <p className="font-medium text-sm">{action.title}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {isTransfer && (
        <TransferModal
          isOpen={isTransfer}
          onClose={() => handleCloseAllModal()}
          selectedStudent={selectedStudent}
        />
      )}

      {/* Collaborate Modal */}
      {isCollaborate && (
        <CollaborateModal
          isOpen={isCollaborate}
          onClose={() => handleCloseAllModal()}
          selectedStudent={selectedStudent}
        />
      )}

      {/* Gmail Modal */}
      {isGmail && (
        <EmailModal
          isOpen={isGmail}
          onClose={() => handleCloseAllModal()}
          selectedStudent={selectedStudent}
        />
      )}

      {/* Follow Up Modal */}
      {isFollowUp && (
        <FollowUpModal
          isOpen={isFollowUp}
          onClose={() => handleCloseAllModal()}
          selectedStudent={selectedStudent}
        />
      )}

      {/* Custom scrollbar styles */}
      {/* You can add this to your global CSS file or in a styled component */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #a1a1a1;
        }
      `}</style>
    </div>
  );
}